import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { register as registerApi, resendVerification, forgotPassword } from '../api/userApi.js';
import { analyticsEvent } from '../utils/analytics.js';
import './LoginPage.css';

const PERKS = [
  { icon: 'coins',    title: '消費 5% 回饋',       desc: '消費金額 5% 回饋點數，1 點 = NT$1 直接折抵' },
  { icon: 'trophy',   title: '限定商品', desc: '部分商品限定會員購買' },
  { icon: 'bell', title: '新品優先通知',       desc: '最新商品到貨第一時間通知' },
];

function LoginPage() {
  const [activeTab, setActiveTab] = useState('login');
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 註冊成功後顯示「請驗證 Email」提示畫面（存剛註冊的 email；非 null 即顯示該畫面）
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [resending, setResending] = useState(false);

  // 忘記密碼 flow（寄重設連結；實際重設在 /reset-password 頁）
  const [fpMode, setFpMode] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpSent, setFpSent] = useState(false);
  const [fpSending, setFpSending] = useState(false);

  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // 來自 PrivateRoute 的「原本要去的頁面」優先；否則依角色預設目的地
  const fromPath = location.state?.from?.pathname;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (activeTab === 'register') {
      if (!name.trim()) { setError('請輸入姓名'); return; }
    }
    if (!email || !email.includes('@')) { setError('請輸入有效的 Email'); return; }
    if (!password || password.length < 8) { setError('密碼至少 8 個字元'); return; }

    setSubmitting(true);
    try {
      if (activeTab === 'login') {
        const user = await login({ email, password });
        analyticsEvent('login', { method: 'email_password' });
        showToast(`歡迎回來，${user.name}`, 'success');
        // 有後台權限的人導去後台；一般會員留在原本頁面（或回首頁）
        const defaultDest = (user.permissions?.length > 0) ? '/admin' : '/';
        navigate(fromPath || defaultDest, { replace: true });
      } else {
        await registerApi({ username: name.trim(), email, password, inviteCode: inviteCode.trim() || undefined });
        // 自動登入（讓未驗證的新會員也能先逛），但不直接跳走，改顯示「請驗證 Email」提示畫面
        await login({ email, password });
        analyticsEvent('sign_up', { method: 'email_password', invite_code_used: Boolean(inviteCode.trim()) });
        setRegisteredEmail(email);
      }
    } catch (err) {
      const msg = err.message || (activeTab === 'login' ? '登入失敗' : '註冊失敗');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFpBack = () => { setFpMode(false); setFpSent(false); };

  const handleForgot = async () => {
    if (fpSending) return;
    if (!fpEmail || !fpEmail.includes('@')) { showToast('請輸入有效的 Email', 'error'); return; }
    setFpSending(true);
    try {
      await forgotPassword(fpEmail.trim());
      setFpSent(true);
    } catch {
      showToast('寄送失敗，請稍後再試', 'error');
    } finally {
      setFpSending(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await resendVerification();
      showToast('驗證信已重新寄出，請收信（含垃圾信匣）', 'success', 4000);
    } catch (err) {
      showToast(`重寄失敗：${err.message}`, 'error', 3000);
    } finally {
      setResending(false);
    }
  };

  if (fpMode) {
    return (
      <main className="auth-page">
        <aside className="auth-side">
          <div>
            <p className="side-eyebrow">JOIN MICHIDART</p>
            <h2 className="side-title">擲出你的<em>道</em><br />從這一刻開始</h2>
          </div>
          <div className="side-foot">
            <span>© 2025 MICHIDART</span><span>·</span><span>道飛鏢工藝專賣</span>
          </div>
        </aside>
        <section className="auth-main">
          <div className="auth-card">
            <button className="fp-back" onClick={handleFpBack}>
              <Icon name="arrow-l" size={14} /> 返回登入
            </button>
            {!fpSent ? (
              <>
                <h2 className="auth-h1">忘記密碼</h2>
                <p className="auth-sub">輸入註冊時使用的 Email，我們會寄一封密碼重設連結給你。</p>
                <div className="field">
                  <label>Email</label>
                  <div className="field-wrap">
                    <Icon name="user" size={18} />
                    <input className="field-input" type="email" placeholder="you@example.com" value={fpEmail}
                      onChange={e => setFpEmail(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleForgot(); }} />
                  </div>
                </div>
                <button className="auth-btn" onClick={handleForgot} disabled={fpSending}>
                  {fpSending ? '寄送中…' : <>寄送重設連結 <Icon name="arrow" size={16} /></>}
                </button>
              </>
            ) : (
              <>
                <h2 className="auth-h1">請收信</h2>
                <p className="auth-sub">
                  如果 <b style={{ color: 'var(--navy)' }}>{fpEmail}</b> 是已註冊的帳號，我們已寄出密碼重設連結（1 小時內有效）。請收信並點連結設定新密碼，記得看垃圾信匣。
                </p>
                <button className="auth-btn" onClick={handleForgot} disabled={fpSending}>
                  {fpSending ? '寄送中…' : '沒收到？重新寄送'}
                </button>
              </>
            )}
          </div>
        </section>
      </main>
    );
  }

  if (registeredEmail) {
    return (
      <main className="auth-page">
        <aside className="auth-side">
          <div>
            <p className="side-eyebrow">JOIN MICHIDART</p>
            <h2 className="side-title">擲出你的<em>道</em><br />從這一刻開始</h2>
          </div>
          <div className="side-foot">
            <span>© 2025 MICHIDART</span><span>·</span><span>道飛鏢工藝專賣</span>
          </div>
        </aside>
        <section className="auth-main">
          <div className="auth-card" style={{ textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#E9F7EF', color: '#1FA463', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
              <Icon name="mail" size={30} />
            </div>
            <h1 className="auth-h1">帳號建立成功！</h1>
            <p className="auth-sub" style={{ marginBottom: 18 }}>
              我們已寄一封驗證信到 <b style={{ color: 'var(--navy)' }}>{registeredEmail}</b>。<br />
              請點信中連結完成 Email 驗證，<b>驗證後才能下單與送出代打登記</b>。
            </p>
            <div style={{ background: '#FFF8E6', border: '1px solid var(--gold, #F5B815)', borderRadius: 12, padding: '12px 16px', font: '600 13px "Noto Sans TC", sans-serif', color: 'var(--ink-strong)', lineHeight: 1.7, marginBottom: 20, textAlign: 'left' }}>
              沒收到信？請稍等幾分鐘並<b>確認垃圾信匣</b>，或按下方重寄。
            </div>
            <button type="button" className="auth-btn" onClick={() => navigate('/')}>
              先去逛逛 <Icon name="arrow" size={16} />
            </button>
            <button type="button" onClick={handleResend} disabled={resending}
              style={{ width: '100%', marginTop: 12, padding: 12, borderRadius: 10, border: '1px solid var(--line, #e7e9ee)', background: '#fff', cursor: resending ? 'wait' : 'pointer', font: '700 14px "Noto Sans TC", sans-serif', color: 'var(--navy)' }}>
              {resending ? '寄送中…' : '重寄驗證信'}
            </button>
            <p className="auth-foot" style={{ marginTop: 16 }}>
              <a onClick={(e) => { e.preventDefault(); navigate('/account'); }} style={{ cursor: 'pointer' }}>前往會員中心</a>
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <aside className="auth-side">
        <div>
          <p className="side-eyebrow">JOIN MICHIDART</p>
          <h2 className="side-title">
            擲出你的<em>道</em><br />從這一刻開始
            <small>FIND YOUR DART · FIND YOUR WAY</small>
          </h2>
        </div>
        <div className="side-perks">
          {PERKS.map(p => (
            <div className="side-perk" key={p.title}>
              <span className="side-perk-i"><Icon name={p.icon} size={20} /></span>
              <div><b>{p.title}</b><small>{p.desc}</small></div>
            </div>
          ))}
        </div>
        <div className="side-foot">
          <span>© 2025 MICHIDART</span>
          <span>·</span>
          <span>道飛鏢工藝專賣</span>
        </div>
      </aside>

      <section className="auth-main">
        <form className="auth-card" onSubmit={handleSubmit}>
          <div className="tabs-pill">
            <button type="button" className={`tab-pill${activeTab === 'login' ? ' on' : ''}`} onClick={() => setActiveTab('login')}>登入</button>
            <button type="button" className={`tab-pill${activeTab === 'register' ? ' on' : ''}`} onClick={() => setActiveTab('register')}>註冊</button>
          </div>

          <h1 className="auth-h1">{activeTab === 'login' ? '歡迎回到 MICHIDART' : '建立 MICHIDART 帳號'}</h1>
          <p className="auth-sub">
            {activeTab === 'login'
              ? '輸入您的帳號與密碼，繼續尋找屬於自己的那一支。'
              : '加入我們，開始您的飛鏢之旅，解鎖會員專屬優惠。'}
          </p>

          {activeTab === 'register' && (
            <div className="field">
              <label>姓名</label>
              <div className="field-wrap">
                <Icon name="user" size={18} />
                <input
                  className="field-input"
                  type="text"
                  placeholder="您的姓名"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>
            </div>
          )}

          <div className="field">
            <label>Email</label>
            <div className="field-wrap">
              <Icon name="user" size={18} />
              <input
                className="field-input"
                type="email"
                placeholder="請輸入信箱"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="field">
            <label>密碼</label>
            <div className="field-wrap">
              <Icon name="lock" size={18} />
              <input
                className="field-input"
                type={showPw ? 'text' : 'password'}
                placeholder="至少 8 個字元"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={activeTab === 'login' ? 'current-password' : 'new-password'}
              />
              <button
                type="button"
                className="field-toggle-pw"
                onClick={() => setShowPw(v => !v)}
                aria-label={showPw ? '隱藏密碼' : '顯示密碼'}
              >
                <Icon name="eye" size={18} />
              </button>
            </div>
          </div>

          {activeTab === 'register' && (
            <div className="field">
              <label>邀請碼（選填）</label>
              <div className="field-wrap">
                <Icon name="coins" size={18} />
                <input
                  className="field-input"
                  type="text"
                  placeholder="輸入好友的推薦碼（選填）"
                  value={inviteCode}
                  onChange={e => setInviteCode(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          {error && (
            <div role="alert" style={{ color: 'var(--red)', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {activeTab === 'login' && (
            <div className="field-row">
              <label className="checkbox"><input type="checkbox" defaultChecked /> 記住我</label>
              <a onClick={e => { e.preventDefault(); setFpMode(true); setFpSent(false); }} style={{ cursor: 'pointer' }}>忘記密碼？</a>
            </div>
          )}

          <button type="submit" className="auth-btn" disabled={submitting}>
            {submitting ? '處理中…' : (activeTab === 'login' ? '登入' : '建立帳號 · 領 50 點')}
            {!submitting && <Icon name="arrow" size={16} />}
          </button>

          <p className="auth-foot">
            {activeTab === 'login'
              ? <>尚未成為 MICHIDART 會員？<a onClick={e => { e.preventDefault(); setActiveTab('register'); }}>免費加入 · 領 50 點</a></>
              : <>已有帳號？<a onClick={e => { e.preventDefault(); setActiveTab('login'); }}>直接登入</a></>}
          </p>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
